import { Reveal, Overline } from "./Reveal";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const services = [
  {
    tag: "01",
    title: "Global Procurement",
    desc: "Rare timepieces, limited editions, and one-of-a-kind objects sourced and delivered discreetly across borders.",
    img: "https://images.unsplash.com/photo-1747995525955-92010650c3ae",
    alt: "Luxury Cartier watch",
  },
  {
    tag: "02",
    title: "VIP Events & Access",
    desc: "Front-row seats, private boxes, fashion weeks, galas and grand-prix paddocks — access reserved for the few.",
    img: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30",
    alt: "Exclusive VIP event celebration",
  },
  {
    tag: "03",
    title: "Fine Dining Reservations",
    desc: "The most coveted tables in the world, secured on your behalf — including those that never take reservations.",
    img: "https://images.pexels.com/photos/17057034/pexels-photo-17057034.jpeg",
    alt: "Sophisticated fine dining table setting",
  },
  {
    tag: "04",
    title: "Bespoke Travel",
    desc: "Private aviation, chartered yachts, and impossible itineraries — orchestrated end to end with absolute care.",
    img: "https://images.unsplash.com/photo-1540962351504-03099e0a754b",
    alt: "Private jet on the tarmac",
  },
];

const Card = ({ s, i }) => (
  <Reveal delay={(i % 2) * 0.12} className={i % 2 === 1 ? "md:mt-24" : ""}>
    <motion.div
      whileHover="hover"
      data-testid={`service-card-${s.tag}`}
      className="group relative overflow-hidden bg-beige cursor-default"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <motion.img
          src={s.img}
          alt={s.alt}
          variants={{ hover: { scale: 1.06 } }}
          transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/10 to-transparent" />
        <span className="absolute top-6 left-6 font-serif text-lg text-ivory/80">
          {s.tag}
        </span>
        <ArrowUpRight
          className="absolute top-6 right-6 text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          size={22}
        />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <h3 className="font-serif font-light text-ivory text-2xl md:text-3xl leading-tight tracking-tight">
            {s.title}
          </h3>
          <motion.p
            variants={{ hover: { opacity: 1, height: "auto", marginTop: 12 } }}
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
            className="overflow-hidden font-sans font-light text-ivory/85 text-sm leading-relaxed"
          >
            {s.desc}
          </motion.p>
        </div>
      </div>
    </motion.div>
  </Reveal>
);

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-ivory py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-y-8 mb-16 md:mb-24">
          <div className="md:col-span-5">
            <Reveal>
              <Overline>What We Orchestrate</Overline>
              <h2 className="mt-6 font-serif font-light text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                An atelier of
                <br />
                <span className="italic text-gold">rare capabilities.</span>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex items-end">
            <Reveal delay={0.15}>
              <p className="font-sans font-light text-navyAccent/80 text-base leading-relaxed">
                Every request is met by a specialist and a network built over
                decades. There is no catalogue — only what you desire, made
                possible.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {services.map((s, i) => (
            <Card key={s.tag} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
